import { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import TimeService from "src/services/timeService/timeService";
import { selectCurrentWorkspaceId } from "src/store/app";
import {
    cancelClientSubscription,
    reviveClientSubscription,
    updateClientExpiration,
    updateClientWorkspace,
    updateWorkspaceMembers,
} from "src/ui/containers/clients/api";
import { ResellerClient, resellerDescriptionMaxLength } from "src/ui/containers/clients/types";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { AddOwnerDialog } from "@dashboard/shared/components/AddOwnerDialog";
import { Box, DialogContentText, Table, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { CellContext, ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { PaneElement } from "../../shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "../../shared/Property";

import { useStyles } from "./WorkspaceTab.styles";

import "react-datepicker/dist/react-datepicker.css";

type OwnerData = {
    id: number;
    email: string;
    role: string;
};

const EmailCell = (props: CellContext<OwnerData, unknown>) => {
    const { email } = props.row.original;

    return (
        <MinimalTableCell align="justify">
            <Typography>{email}</Typography>
        </MinimalTableCell>
    );
};

type ActionCellProps = CellContext<OwnerData, unknown> & {
    workspaceId: number | null;
    workspaceName: string;
    clientId: number;
    onOwnerRemoved: () => Promise<void>;
};

const ActionCell = ({ row, workspaceId, workspaceName, clientId, onOwnerRemoved }: ActionCellProps) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { t } = useTranslation(["common", "modals", "sidepanels"]);
    const { email, role } = row.original;

    const onConfirmDelete = async () => {
        if (workspaceId === null) {
            return;
        }

        try {
            await updateWorkspaceMembers({
                workspaceId,
                clientId,
                removedMembers: [email],
            });
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.ownerRemovedSuccess"));
            await onOwnerRemoved();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.ownerRemovedFailed"));
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    return (
        <MinimalTableCell align="right">
            <DangerOutlinedButton onClick={() => setIsDeleteModalOpen(true)}>
                <i className="fa-solid fa-trash" />
            </DangerOutlinedButton>
            <ConfirmDialog
                title={t("modals:removeMember.title")}
                dangerButton
                primaryActionText={t("sidepanels:clientsPanel.workspaceTab.button.remove")}
                open={isDeleteModalOpen}
                onConfirm={onConfirmDelete}
                onClose={() => setIsDeleteModalOpen(false)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="removeMember.descriptionWorkspace"
                        values={{
                            email,
                            role,
                            name: workspaceName,
                        }}
                        components={{
                            bold: <span style={{ fontWeight: "bold" }} />,
                        }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};

const createOwnerColumns = (
    workspaceId: number | null,
    workspaceName: string,
    clientId: number,
    onOwnerRemoved: () => Promise<void>,
): ColumnDef<OwnerData, unknown>[] => [
    {
        id: "email",
        header: i18next.t("common:label.email"),
        cell: EmailCell,
        enableSorting: true,
        accessorKey: "email",
        sortingFn: "alphanumeric",
    },
    {
        id: "action",
        header: "",
        cell: (cellProps) => (
            <ActionCell
                {...cellProps}
                workspaceId={workspaceId}
                workspaceName={workspaceName}
                clientId={clientId}
                onOwnerRemoved={onOwnerRemoved}
            />
        ),
    },
];

const getStatusConfig = (status: string, theme: Theme) => {
    switch (status) {
        case "active":
            return {
                color: theme.palette.emteria.success,
                icon: <i className="fa-solid fa-circle-check" />,
                text: i18next.t("common:status.active"),
            };
        case "expired":
            return {
                color: theme.palette.emteria.warning,
                icon: <i className="fa-solid fa-circle-exclamation" />,
                text: i18next.t("common:status.expired"),
            };
        case "terminated":
            return {
                color: theme.palette.emteria.error,
                icon: <i className="fa-solid fa-circle-exclamation" />,
                text: i18next.t("common:status.terminated"),
            };
        case "cancelled":
            return {
                color: theme.palette.emteria.error,
                icon: <i className="fa-solid fa-triangle-exclamation" />,
                text: i18next.t("common:status.cancelled"),
            };
        default:
            return {
                color: theme.palette.emteria.background.paperDeep,
                icon: <i className="fa-solid fa-circle-check" />,
                text: status,
            };
    }
};

type ClientWorkspaceEdit = {
    organizationName?: string;
    resellerDescription?: string;
};

const WorkspaceTab = (props: {
    workspace: ResellerClient;
    refetch: () => Promise<void>;
    refetchClientsList?: () => Promise<void>;
}) => {
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);
    const { workspace, refetch, refetchClientsList } = props;
    const { t } = useTranslation(["common", "modals", "sidepanels"]);
    const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const theme = useTheme();
    const { classes } = useStyles();

    const subscription = workspace.subscription;

    const handleCancelSubscription = () => {
        setCancelSubscriptionDialogOpen(true);
    };

    const handleReviveSubscription = async () => {
        if (currentWorkspaceId === null) {
            return;
        }

        try {
            await reviveClientSubscription({
                workspaceId: currentWorkspaceId,
                clientId: workspace.id,
            });
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionRevivedSuccess"));
            await refetch();
            await refetchClientsList?.();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionRevivedFailed"));
        }
    };

    const confirmCancelSubscription = async () => {
        if (currentWorkspaceId === null) {
            return;
        }

        try {
            const currentDate = new Date().toISOString();
            await cancelClientSubscription({
                workspaceId: currentWorkspaceId,
                clientId: workspace.id,
                date: currentDate,
            });
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionCancelledSuccess"));
            await refetch();
            await refetchClientsList?.();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionCancelledFailed"));
        }
    };

    const handleSaveExpirationDate = async ({ dateString }: { dateString: string | null }) => {
        if (dateString && currentWorkspaceId !== null) {
            try {
                await updateClientExpiration({
                    workspaceId: currentWorkspaceId,
                    clientId: workspace.id,
                    date: dateString,
                });
                showSuccessToast(
                    t("sidepanels:clientsPanel.workspaceTab.toast.expirationDateUpdated", {
                        date: dateString,
                    }),
                );

                await refetch();
                await refetchClientsList?.();
            } catch (_error) {
                const err = _error as Error;
                showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.expirationDateUpdateFailed"));
            }
        }
    };

    /**
     * Sends `name` — which the endpoint documents as required — plus only the field that changed. The backend
     * patches per field, so a narrow body avoids re-validating `organizationUrl` and `defaultGroupId` (which
     * must name a live group, and is 0 when unset); a description edit has no business failing on either. It
     * also keeps us from echoing back a stale copy of fields somebody else may have just changed.
     */
    const saveClientWorkspace = async (edit: ClientWorkspaceEdit) => {
        if (currentWorkspaceId === null) {
            return;
        }

        try {
            await updateClientWorkspace({
                workspaceId: currentWorkspaceId,
                clientId: workspace.id,
                name: workspace.name,
                ...edit,
            });
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.updateSuccess"));
            await refetch();
            await refetchClientsList?.();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.updateFailed"));
        }
    };

    const owners = useMemo(
        () =>
            workspace.members
                ?.filter((member) => member.role === "Owner")
                .map((owner, index) => ({
                    id: index,
                    email: owner.email,
                    role: owner.role,
                })) || [],
        [workspace.members],
    );

    const handleOwnerRemoved = useCallback(async () => {
        await refetch();
        await refetchClientsList?.();
    }, [refetch, refetchClientsList]);

    const table = useReactTable({
        data: owners,
        columns: createOwnerColumns(currentWorkspaceId, workspace.name, workspace.id, handleOwnerRemoved),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const statusConfig = getStatusConfig(subscription?.status || "", theme);

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:clientsPanel.workspaceTab.subscriptionDetails")}</PaneElement.Header>

                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("common:label.organizationName")}</PropertyKey>
                    <PropertyValue style={{ display: "block" }}>
                        <PaneElement.EditableInput
                            initialValue={workspace?.organizationName ?? ""}
                            onSave={(value) => saveClientWorkspace({ organizationName: value })}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("common:label.description")}</PropertyKey>
                    <PropertyValue style={{ display: "block" }}>
                        <PaneElement.EditableInput
                            initialValue={workspace?.resellerDescription ?? ""}
                            onSave={(value) => saveClientWorkspace({ resellerDescription: value })}
                            multiline
                            minRows={3}
                            maxLength={resellerDescriptionMaxLength}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("sidepanels:clientsPanel.workspaceTab.status")}</PropertyKey>
                    <PropertyValue>
                        <Typography color={theme.palette.emteria.text.primary} variant="body1">
                            {statusConfig.text}
                        </Typography>
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("sidepanels:clientsPanel.workspaceTab.chargeId")}</PropertyKey>
                    <PropertyValue>
                        <Typography variant="body1">{subscription?.chargeId || "-"}</Typography>
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("common:label.expirationDate")}</PropertyKey>
                    <PropertyValue style={{ display: "block" }}>
                        <PaneElement.EditableDateInput
                            initialValue={subscription?.expirationDate}
                            onSave={handleSaveExpirationDate}
                            placeholder={t("sidepanels:clientsPanel.workspaceTab.selectExpirationDate")}
                        />
                    </PropertyValue>
                </PropertyRow>

                {subscription?.cancellationDate && (
                    <PropertyRow className={classes.row}>
                        <PropertyKey>{t("sidepanels:clientsPanel.workspaceTab.cancellationDate")}</PropertyKey>
                        <PropertyValue>
                            <Typography variant="body1">
                                {TimeService.getLocalDate(subscription.cancellationDate, "ISO", "date")}
                            </Typography>
                        </PropertyValue>
                    </PropertyRow>
                )}

                <Box className={classes.subscriptionButtonContainer}>
                    {subscription?.status === "Cancelled" || subscription?.cancellationDate ? (
                        <button className={classes.reviveButton} onClick={handleReviveSubscription}>
                            <i className="fa-solid fa-play" />
                            {t("sidepanels:clientsPanel.workspaceTab.reviveSubscription")}
                        </button>
                    ) : (
                        <DangerOutlinedButton
                            onClick={handleCancelSubscription}
                            startIcon={<i className="fa-solid fa-ban" />}
                            size="medium"
                        >
                            {t("sidepanels:clientsPanel.workspaceTab.cancelSubscription")}
                        </DangerOutlinedButton>
                    )}
                </Box>
            </PaneElement.Section>

            <PaneElement.Section>
                <Box className={classes.ownersHeader}>
                    <PaneElement.Header>{t("sidepanels:clientsPanel.workspaceTab.workspaceOwners")}</PaneElement.Header>
                    <Box className={classes.ownersActions}>
                        <SecondaryButton onClick={() => setDialogOpen(true)} style={{ marginLeft: theme.spacing(2) }}>
                            <i className="fa-solid fa-user-plus" />
                        </SecondaryButton>
                    </Box>
                </Box>
                <PaneElement.TableContainer>
                    <Table stickyHeader>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Header key={header.id} {...header} />
                                    ))}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody table={table} noDataMessage={t("sidepanels:clientsPanel.workspaceTab.noOwnersFound")}>
                            {table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) =>
                                        flexRender(cell.column.columnDef.cell, {
                                            ...cell.getContext(),
                                            key: cell.id,
                                        }),
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </PaneElement.TableContainer>
            </PaneElement.Section>

            <AddOwnerDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                workspaceId={currentWorkspaceId}
                clientId={workspace.id}
                onOwnerAdded={async () => {
                    await refetch();
                    await refetchClientsList?.();
                    setDialogOpen(false);
                }}
            />

            <ConfirmDialog
                title={t("modals:cancelSubscription.title")}
                open={cancelSubscriptionDialogOpen}
                onConfirm={confirmCancelSubscription}
                onClose={() => setCancelSubscriptionDialogOpen(false)}
                dangerButton
                primaryActionText={t("common:button.confirm")}
            >
                <DialogContentText>{t("modals:cancelSubscription.description")}</DialogContentText>
            </ConfirmDialog>
        </PaneElement.Container>
    );
};

export default WorkspaceTab;
